import { storeToRefs } from 'pinia';
import { useCartStore } from '../stores/cart';
import {
  AddCartItemsOptions,
  AddCartItemsResult,
  CartItemChangeResult,
  CartItemQuantitiesInput,
  CartItemQuantity,
  CartActionPlan,
  CartStockRemoval,
  CartUrlActionItem,
  CartUrlActionOptions,
  CartUrlNotice,
  CartUrlNoticeItem,
} from '../types/models';
import { AddToCartItem, ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import { ShopifyCartRequestError, useShopifyCartService } from './shopifyCart';
import { enqueueCartMutation } from './cartMutationQueue';
import { getLineIdentity, getLineQuantities } from './lineItemProperties';
import {
  areRemovalsCurrent,
  fitsProjectedCart,
  getPreAddLineQuantities,
  getRestoreItems,
  groupStockLines,
  hasReleasedCapacity,
  planRemovalSwap,
  planStockSwap,
  sharesTrackedStock,
  type PendingStockSwap,
  type StockSwap,
} from './cartStockSwap';
import {
  clearCartUrlActionRecord,
  createCartUrlActionRecord,
  getCartUrlActionItems,
  getCartUrlActionUndo,
  hasCartUrlActionAdditions,
  hasUnexpectedCartChanges,
  isCartUrlActionComplete,
  readCartUrlActionRecord,
  writeCartUrlActionRecord,
  type CartUrlActionRecord,
} from './cartUrlAction';
import { useCartValidation } from '../composables/useCartValidation';
import { findVariantParentMaxQuantityFamilies } from '../cartRules/variantParentMaxQuantity';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import {
  getAddStockDemand,
  getCartStockUsage,
} from '@/features/Product/services/productAvailability';

const CART_RESYNC_DELAY = 2000;
const MAX_CART_RESYNC_ATTEMPTS = 3;
const STOCK_REJECTION_STATUS = 422;
const STOCK_RECHECK_DELAY = 3000;

type CartMutationResult = {
  error: unknown;
  cartBefore: ShopifyCart | null;
  fetchedCart: ShopifyCart | null;
};

let isCartStale = false;
let cartResyncAttempts = 0;
let cartResyncTimer: ReturnType<typeof setTimeout> | null = null;
let stockRecheckTimer: ReturnType<typeof setTimeout> | null = null;
let pendingStockSwap: PendingStockSwap | null = null;
let pendingStockCorrection: Map<string, CartStockRemoval> | null = null;
let activeCartUrlActionId: string | null = null;

const isStockRejectionError = (error: unknown): boolean =>
  error instanceof ShopifyCartRequestError && error.status === STOCK_REJECTION_STATUS;

const getUntargetedRemovals = (
  cartBefore: ShopifyCart,
  cartAfter: ShopifyCart,
  isTargeted: (item: ShopifyCartItem) => boolean,
): CartStockRemoval[] =>
  cartBefore.items.flatMap((item) => {
    if (isTargeted(item)) return [];
    const quantityAfter = cartAfter.items.find(({ key }) => key === item.key)?.quantity ?? 0;
    const removedQuantity = item.quantity - quantityAfter;
    return removedQuantity > 0 ? [{ title: item.title, quantity: removedQuantity }] : [];
  });

const getLineTitles = (items: ShopifyCartItem[]): Map<string, string> =>
  new Map(items.map((item) => [getLineIdentity(item.variant_id, item.properties), item.title]));

const getLinesByIdentity = (items: ShopifyCartItem[]): Map<string, ShopifyCartItem> =>
  new Map(items.map((item) => [getLineIdentity(item.variant_id, item.properties), item]));

const getAddedQuantities = (
  items: AddToCartItem[],
  cartBefore: ShopifyCart,
  cartAfter: ShopifyCart,
): number[] => {
  const quantitiesBefore = getLineQuantities(cartBefore.items);
  const unclaimedQuantities = new Map(
    [...getLineQuantities(cartAfter.items)].map(([lineIdentity, quantity]) => [
      lineIdentity,
      Math.max(0, quantity - (quantitiesBefore.get(lineIdentity) ?? 0)),
    ]),
  );
  return items.map(({ id, properties, quantity }) => {
    const lineIdentity = getLineIdentity(id, properties);
    const unclaimedQuantity = unclaimedQuantities.get(lineIdentity) ?? 0;
    const addedQuantity = Math.min(unclaimedQuantity, quantity);
    unclaimedQuantities.set(lineIdentity, unclaimedQuantity - addedQuantity);
    return addedQuantity;
  });
};

const getFailedAddResult = (
  items: AddToCartItem[],
  error: unknown,
  cartBefore: ShopifyCart | null,
  cartAfter: ShopifyCart | null,
): AddCartItemsResult => {
  const isStockRejection = isStockRejectionError(error);
  if (!cartBefore || !cartAfter) {
    return { outcome: 'unconfirmed', addedQuantities: items.map(() => 0), isStockRejection };
  }
  const addedQuantities = getAddedQuantities(items, cartBefore, cartAfter);
  if (addedQuantities.every((quantity) => quantity === 0)) {
    return { outcome: 'rejected', addedQuantities, isStockRejection };
  }
  if (addedQuantities.every((quantity, index) => quantity >= items[index].quantity)) {
    return { outcome: 'complete', addedQuantities, isStockRejection: false };
  }
  return { outcome: 'partial', addedQuantities, isStockRejection };
};

const getReducedLineQuantities = (
  cart: ShopifyCart,
  updates: CartItemQuantity[],
): Map<string, number> =>
  updates.reduce((quantities, { key, quantity }) => {
    const line = cart.items.find((item) => item.key === key);
    if (!line || quantity >= line.quantity) return quantities;
    return quantities.set(getLineIdentity(line.variant_id, line.properties), line.quantity);
  }, new Map<string, number>());

export const cartActions = () => {
  const { DEFAULT_EMPTY_CART, updateCart, changeCartItem, getCart, clearCart, addToCart } =
    useShopifyCartService();
  const cartStore = useCartStore();
  const {
    setCart,
    addCartStockRemovals,
    setCartAddLimitedByStock,
    startCartAdd,
    setLatestAddLineIdentities,
    setCartUrlNotice,
    setCartUrlActionPending,
    isFreeGiftItem,
  } = cartStore;
  const { cartData, cartDataLoaded, latestAddLineIdentities, cartUrlNotice } =
    storeToRefs(cartStore);
  const { validateCart } = useCartValidation();
  const productStockStore = useProductStockStore();
  const {
    refreshStock,
    getExactStock,
    checkAddStockWithRedraws,
    getStockFittedAddQuantity,
    getUnavailableComponentTitles,
  } = productStockStore;
  const { unavailableLines } = storeToRefs(productStockStore);

  const scheduleCartResync = () => {
    if (cartResyncTimer || cartResyncAttempts >= MAX_CART_RESYNC_ATTEMPTS) return;
    cartResyncAttempts += 1;
    cartResyncTimer = setTimeout(() => {
      cartResyncTimer = null;
      refreshCart();
    }, CART_RESYNC_DELAY);
  };

  const trackStockCorrection = (quantitiesBefore: Map<string, number>) => {
    if (!quantitiesBefore.size) return;
    const cartLines = getLinesByIdentity(cartData.value.items);
    const linesBefore = [...quantitiesBefore].map(
      ([lineIdentity, quantity]): [string, CartStockRemoval] => {
        const line = cartLines.get(lineIdentity);
        const unavailableComponentTitles = line
          ? getUnavailableComponentTitles({
              variantId: line.variant_id,
              properties: line.properties,
            })
          : [];
        return [lineIdentity, { title: line?.title ?? '', quantity, unavailableComponentTitles }];
      },
    );
    pendingStockCorrection = new Map([...linesBefore, ...(pendingStockCorrection ?? [])]);
  };

  const resolveStockCorrection = (cart: ShopifyCart) => {
    if (!pendingStockCorrection) return;
    const quantitiesAfter = getLineQuantities(cart.items);
    const titlesAfter = getLineTitles(cart.items);
    const removals = [...pendingStockCorrection].flatMap(([lineIdentity, lineBefore]) => {
      const removedQuantity = lineBefore.quantity - (quantitiesAfter.get(lineIdentity) ?? 0);
      if (removedQuantity <= 0) return [];
      const title = lineBefore.title || titlesAfter.get(lineIdentity) || '';
      const { unavailableComponentTitles } = lineBefore;
      return [{ title, quantity: removedQuantity, unavailableComponentTitles }];
    });
    pendingStockCorrection = null;
    if (removals.length) addCartStockRemovals(removals);
  };

  const isStockRejectionExplained = (
    items: AddToCartItem[],
    cartItemsBefore: ShopifyCartItem[],
  ): boolean => {
    const cartUsage = getCartStockUsage(cartItemsBefore);
    return [...getAddStockDemand(items)].some(([variantId, demand]) => {
      const exactStock = getExactStock(variantId);
      return exactStock !== undefined && demand + (cartUsage.get(variantId) ?? 0) > exactStock;
    });
  };

  const refreshStockAfterRejection = (
    items: AddToCartItem[],
    cartItemsBefore: ShopifyCartItem[],
  ) => {
    const isExplained = isStockRejectionExplained(items, cartItemsBefore);
    refreshStock({ force: true });
    if (isExplained || stockRecheckTimer) return;
    stockRecheckTimer = setTimeout(() => {
      stockRecheckTimer = null;
      refreshStock({ force: true });
    }, STOCK_RECHECK_DELAY);
  };

  const settleStockSwap = async (cart: ShopifyCart): Promise<ShopifyCart> => {
    const settlement = pendingStockSwap;
    if (!settlement) return cart;
    const { swap, items, releasedCart, isAddSent, acceptedQuantities } = settlement;
    if (isAddSent && !acceptedQuantities && !releasedCart) return cart;
    pendingStockSwap = null;
    const confirmedQuantities =
      acceptedQuantities ??
      (isAddSent && releasedCart
        ? getAddedQuantities(items, releasedCart, cart)
        : items.map(() => 0));
    trackStockCorrection(getPreAddLineQuantities(swap, confirmedQuantities));
    const restoreItems = getRestoreItems(swap, confirmedQuantities, cart);
    if (!restoreItems.length) return cart;
    await addToCart(restoreItems).catch((error: unknown) =>
      console.error('Failed to restore released cart items', error),
    );
    return getCart();
  };

  const getUnresolvedCartUrlActionRecord = (): CartUrlActionRecord | null => {
    const record = readCartUrlActionRecord();
    return record && record.id !== activeCartUrlActionId ? record : null;
  };

  const markCartUrlActionPending = (notice: CartUrlNotice) => {
    setCartUrlActionPending(true);
    setCartUrlNotice(notice);
  };

  const finishCartUrlAction = (notice: CartUrlNotice | null) => {
    clearCartUrlActionRecord();
    setCartUrlActionPending(false);
    setCartUrlNotice(notice);
  };

  const dismissCartUrlNotice = () => {
    if (activeCartUrlActionId) return;
    if (cartUrlNotice.value || readCartUrlActionRecord()) finishCartUrlAction(null);
  };

  const resolveCartUrlActionRecord = (record: CartUrlActionRecord, cart: ShopifyCart) => {
    if (record.phase === 'prepared') {
      finishCartUrlAction({ type: 'not-added', items: [] });
      return;
    }
    if (cart.token !== record.token) {
      finishCartUrlAction({ type: 'cart-changed', items: [] });
      return;
    }
    const items = getCartUrlActionItems(record, cart);
    if (hasUnexpectedCartChanges(record, cart, isFreeGiftItem)) {
      markCartUrlActionPending({ type: 'cart-changed', items });
      return;
    }
    if (isCartUrlActionComplete(items)) {
      finishCartUrlAction({ type: 'added', items });
      return;
    }
    markCartUrlActionPending({
      type: hasCartUrlActionAdditions(items) ? 'partly-added' : 'unconfirmed',
      items,
    });
  };

  const syncCart = async (): Promise<ShopifyCart | null> => {
    const cartUrlActionRecord = getUnresolvedCartUrlActionRecord();
    if (cartUrlActionRecord) {
      markCartUrlActionPending(cartUrlNotice.value ?? { type: 'unconfirmed', items: [] });
    }
    try {
      const fetchedCart = await settleStockSwap(await getCart());
      if (cartUrlActionRecord) resolveCartUrlActionRecord(cartUrlActionRecord, fetchedCart);
      resolveStockCorrection(fetchedCart);
      setCart(await validateCart(fetchedCart));
      isCartStale = false;
      cartResyncAttempts = 0;
      return fetchedCart;
    } catch (error) {
      console.error('Failed to sync cart', error);
      if (!cartDataLoaded.value) setCart(DEFAULT_EMPTY_CART());
      isCartStale = true;
      scheduleCartResync();
      return null;
    }
  };

  const prepareCartForMutation = async (): Promise<boolean> => {
    if (!cartDataLoaded.value || isCartStale || pendingStockSwap) await syncCart();
    return !pendingStockSwap;
  };

  const mutateAndSync = async (
    mutate: () => Promise<ShopifyCart | void>,
    isTargeted: (item: ShopifyCartItem) => boolean,
  ): Promise<CartMutationResult> => {
    const cartBefore = isCartStale ? null : cartData.value;
    const { error, responseCart } = await mutate().then(
      (cart) => ({ error: null, responseCart: cart ?? null }),
      (mutationError: unknown) => ({ error: mutationError, responseCart: null }),
    );
    const fetchedCart = await syncCart();
    if (!fetchedCart && responseCart) resolveStockCorrection(responseCart);
    const result = { error, cartBefore, fetchedCart };
    if (!cartBefore || !fetchedCart) return result;
    const removals = getUntargetedRemovals(cartBefore, fetchedCart, isTargeted);
    if (removals.length) addCartStockRemovals(removals);
    return result;
  };

  const performCartMutation = async (
    mutate: () => Promise<ShopifyCart | void>,
    isTargeted: (item: ShopifyCartItem) => boolean,
  ): Promise<CartMutationResult> => {
    if (!(await prepareCartForMutation())) {
      return {
        error: new Error('A previous add is still being settled'),
        cartBefore: null,
        fetchedCart: null,
      };
    }
    return mutateAndSync(mutate, isTargeted);
  };

  const runCartMutation = (
    mutate: () => Promise<ShopifyCart | void>,
    isTargeted: (item: ShopifyCartItem) => boolean,
  ): Promise<CartMutationResult> =>
    enqueueCartMutation(() => performCartMutation(mutate, isTargeted));

  const refreshCart = (): Promise<ShopifyCart> =>
    enqueueCartMutation(async () => {
      await syncCart();
      return cartData.value;
    });

  const prepareStockSwap = (items: AddToCartItem[], cart: ShopifyCart): StockSwap | null => {
    const getStockLines = () =>
      groupStockLines(items, cart, {
        isFreeGiftItem,
        unavailableLineKeys: new Set(unavailableLines.value),
        protectedLineIdentities: new Set(latestAddLineIdentities.value),
      });
    const isExactStock = (variantId: number) => getExactStock(variantId) !== undefined;
    if (!sharesTrackedStock(getStockLines(), isExactStock)) return null;
    return planStockSwap(getStockLines(), getExactStock);
  };

  const runStockSwap = async (
    items: AddToCartItem[],
    swap: StockSwap,
  ): Promise<AddCartItemsResult> => {
    const releaseUpdates = Object.fromEntries(
      swap.releases.map(({ key, quantity }) => [key, quantity]),
    );
    const release = await updateCart(releaseUpdates).then(
      (cart) => ({ cart, isRejected: false }),
      (error: unknown) => {
        console.error('Failed to release cart items for a new add', error);
        return { cart: null, isRejected: error instanceof ShopifyCartRequestError };
      },
    );
    const notAdded = (isStockRejection: boolean): AddCartItemsResult => ({
      outcome: 'rejected',
      addedQuantities: items.map(() => 0),
      isStockRejection,
    });
    if (release.isRejected) {
      await syncCart();
      return notAdded(false);
    }
    const settlement: PendingStockSwap = {
      swap,
      items,
      releasedCart: null,
      isAddSent: false,
      acceptedQuantities: null,
    };
    pendingStockSwap = settlement;
    const releasedCart = (await getCart().catch(() => null)) ?? release.cart;
    if (!releasedCart) {
      await syncCart();
      return notAdded(false);
    }
    if (!hasReleasedCapacity(swap, releasedCart)) {
      refreshStock({ force: true });
      if (!(await syncCart()) && pendingStockSwap === settlement) {
        await settleStockSwap(releasedCart).catch((error: unknown) =>
          console.error('Failed to confirm restored cart items', error),
        );
      }
      return notAdded(true);
    }
    settlement.releasedCart = releasedCart;
    settlement.isAddSent = true;
    const addError = await addToCart(items).then(
      () => null,
      (error: unknown) => error,
    );
    if (!addError) settlement.acceptedQuantities = items.map(({ quantity }) => quantity);
    const fetchedCart = (await syncCart()) ?? (addError ? await syncCart() : null);
    if (!addError) {
      return {
        outcome: 'complete',
        addedQuantities: items.map(({ quantity }) => quantity),
        isStockRejection: false,
      };
    }
    console.error('Failed to add items to cart', addError);
    if (isStockRejectionError(addError)) refreshStockAfterRejection(items, releasedCart.items);
    return getFailedAddResult(items, addError, releasedCart, fetchedCart);
  };

  const runAdd = async (items: AddToCartItem[]): Promise<AddCartItemsResult> => {
    const addedLineIdentities = new Set(
      items.map(({ id, properties }) => getLineIdentity(id, properties)),
    );
    const { error, cartBefore, fetchedCart } = await mutateAndSync(
      async () => {
        await addToCart(items);
      },
      (item) => addedLineIdentities.has(getLineIdentity(item.variant_id, item.properties)),
    );
    if (!error) {
      return {
        outcome: 'complete',
        addedQuantities: items.map(({ quantity }) => quantity),
        isStockRejection: false,
      };
    }
    console.error('Failed to add items to cart', error);
    if (isStockRejectionError(error)) refreshStockAfterRejection(items, cartBefore?.items ?? []);
    return getFailedAddResult(items, error, cartBefore, fetchedCart);
  };

  const runActionPlan = async (
    items: AddToCartItem[],
    actionPlan: CartActionPlan,
  ): Promise<AddCartItemsResult> => {
    const notAdded = (isStockRejection: boolean): AddCartItemsResult => ({
      outcome: 'rejected',
      addedQuantities: items.map(() => 0),
      isStockRejection,
    });
    const cart = cartData.value;
    if (isCartStale || !areRemovalsCurrent(cart, actionPlan.removals)) return notAdded(false);
    const fit = fitsProjectedCart(
      items,
      cart,
      actionPlan,
      getExactStock,
      findVariantParentMaxQuantityFamilies(),
    );
    if (fit !== 'fits') return notAdded(fit === 'stock-short');
    const swap = planRemovalSwap(items, cart, actionPlan.removals, getExactStock);
    return swap ? runStockSwap(items, swap) : runAdd(items);
  };

  const runOrdinaryAdd = async (items: AddToCartItem[]): Promise<AddCartItemsResult> => {
    const stockSwap = isCartStale ? null : prepareStockSwap(items, cartData.value);
    return stockSwap ? runStockSwap(items, stockSwap) : runAdd(items);
  };

  const runStockLimitedAdd = async (items: AddToCartItem[]): Promise<AddCartItemsResult> => {
    const quantity = items.length === 1 ? getStockFittedAddQuantity(items[0]) : 0;
    if (quantity <= 0) {
      return { outcome: 'rejected', addedQuantities: items.map(() => 0), isStockRejection: true };
    }
    const result = await runOrdinaryAdd([{ ...items[0], quantity }]);
    if (result.outcome !== 'complete') return result;
    return { ...result, outcome: 'partial', isStockRejection: true };
  };

  const addCartItems = (
    items: AddToCartItem[],
    { actionPlan, redrawItems }: AddCartItemsOptions = {},
  ): Promise<AddCartItemsResult> => {
    startCartAdd();
    dismissCartUrlNotice();
    return enqueueCartMutation(async () => {
      const notAdded = (isStockRejection: boolean): AddCartItemsResult => ({
        outcome: 'rejected',
        addedQuantities: items.map(() => 0),
        isStockRejection,
      });
      if (!(await prepareCartForMutation())) return notAdded(false);
      const { stockCheck, items: checkedItems } = await checkAddStockWithRedraws(
        items,
        redrawItems,
      );
      if (stockCheck === 'not-refreshed') return notAdded(false);
      if (stockCheck === 'unavailable' && actionPlan) return notAdded(true);
      const runCheckedAdd = stockCheck === 'available' ? runOrdinaryAdd : runStockLimitedAdd;
      const result = actionPlan
        ? await runActionPlan(checkedItems, actionPlan)
        : await runCheckedAdd(checkedItems);
      if (result.outcome === 'partial' && result.isStockRejection) setCartAddLimitedByStock(true);
      const addedLineIdentities = checkedItems
        .filter((_, index) => result.addedQuantities[index] > 0)
        .map(({ id, properties }) => getLineIdentity(id, properties));
      if (addedLineIdentities.length) setLatestAddLineIdentities(addedLineIdentities);
      return result;
    });
  };

  const assertItemInCart = (key: string) => {
    if (!cartData.value.items.some((item) => key === item.key)) {
      throw new Error(`Item with key ${key} doesn't exist in cart`);
    }
  };

  const updateCartItemQuantities = async (
    input: CartItemQuantitiesInput,
    { isStockCorrection = false }: { isStockCorrection?: boolean } = {},
  ): Promise<ShopifyCart> => {
    let updatedKeys = new Set<string>();
    const { error } = await runCartMutation(
      async () => {
        const items = typeof input === 'function' ? input(cartData.value) : input;
        updatedKeys = new Set(items.map(({ key }) => key));
        if (!items.length) return;
        const updates: Record<string, number> = items.reduce((updateObject, { key, quantity }) => {
          assertItemInCart(key);
          updateObject[key] = quantity;
          return updateObject;
        }, {});
        if (isStockCorrection) {
          trackStockCorrection(getReducedLineQuantities(cartData.value, items));
        }
        return updateCart(updates);
      },
      (item) => updatedKeys.has(item.key),
    );
    if (error) console.error(error);
    return cartData.value;
  };

  const changeCartItemQuantity = async ({
    key,
    quantity,
  }: CartItemQuantity): Promise<CartItemChangeResult> => {
    dismissCartUrlNotice();
    const { error, cartBefore, fetchedCart } = await runCartMutation(
      async () => {
        assertItemInCart(key);
        await changeCartItem(key, quantity);
      },
      (item) => item.key === key,
    );
    if (error) console.error(error);
    const isStockRejection = isStockRejectionError(error);
    if (isStockRejection) {
      const line = cartBefore?.items.find((item) => item.key === key);
      refreshStockAfterRejection(
        line ? [{ id: line.variant_id, quantity, properties: line.properties }] : [],
        cartBefore?.items.filter((item) => item.key !== key) ?? [],
      );
    }
    const getLineQuantity = (cart: ShopifyCart) =>
      cart.items.find((item) => item.key === key)?.quantity ?? 0;
    return {
      error,
      isStockRejection,
      isConfirmed: !!fetchedCart,
      isLimitedByCartRules:
        !!fetchedCart &&
        getLineQuantity(fetchedCart) >= quantity &&
        getLineQuantity(cartData.value) < quantity,
    };
  };

  const markCartUrlActionUnconfirmed = (items: CartUrlNoticeItem[]): boolean => {
    markCartUrlActionPending({
      type: hasCartUrlActionAdditions(items) ? 'partly-added' : 'unconfirmed',
      items,
    });
    return false;
  };

  const undoCartUrlAction = async (record: CartUrlActionRecord): Promise<boolean> => {
    const cart = await getCart().catch((error: unknown) => {
      console.error('Failed to read the cart before removing URL cart items', error);
      return null;
    });
    if (!cart) return markCartUrlActionUnconfirmed([]);
    const updates = getCartUrlActionUndo(record, cart);
    if (Object.keys(updates).length) {
      await updateCart(updates).catch((error: unknown) =>
        console.error('Failed to remove URL cart items', error),
      );
    }
    if (!(await syncCart())) return markCartUrlActionUnconfirmed([]);
    const items = getCartUrlActionItems(record, cartData.value);
    if (hasCartUrlActionAdditions(items)) return markCartUrlActionUnconfirmed(items);
    if (hasUnexpectedCartChanges(record, cartData.value, isFreeGiftItem)) {
      clearCartUrlActionRecord();
      markCartUrlActionPending({ type: 'cart-changed', items });
      return false;
    }
    finishCartUrlAction({ type: 'not-added', items: [] });
    return false;
  };

  const runCartUrlAction = async (
    record: CartUrlActionRecord,
    items: AddToCartItem[],
    isRequestedCartSettled: (cart: ShopifyCart) => boolean,
  ): Promise<boolean> => {
    const requestedIdentities = new Set(record.requested.map(({ identity }) => identity));
    const { error, fetchedCart } = await mutateAndSync(
      async () => {
        if (!writeCartUrlActionRecord({ ...record, phase: 'sent' })) {
          throw new Error('Could not save the cart URL action record');
        }
        await addToCart(items);
      },
      (item) => requestedIdentities.has(getLineIdentity(item.variant_id, item.properties)),
    );
    if (error) console.error('Failed to add URL cart items', error);
    if (!fetchedCart) return markCartUrlActionUnconfirmed([]);
    const isStockRefreshed = await refreshStock({ force: true });
    const cart = cartData.value;
    const isAdded =
      isStockRefreshed &&
      isCartUrlActionComplete(getCartUrlActionItems(record, cart)) &&
      !hasUnexpectedCartChanges(record, cart, isFreeGiftItem) &&
      isRequestedCartSettled(cart);
    if (!isAdded) return undoCartUrlAction(record);
    finishCartUrlAction(null);
    return true;
  };

  const addCartUrlItems = (
    urlItems: CartUrlActionItem[],
    { findUnavailableTitles, isRequestedCartSettled }: CartUrlActionOptions,
  ): Promise<boolean> =>
    enqueueCartMutation(async () => {
      const isCartSynced = !!(await syncCart()) && !pendingStockSwap;
      if (readCartUrlActionRecord()) return false;
      if (!isCartSynced) {
        setCartUrlNotice({ type: 'not-added', items: [] });
        return false;
      }
      const cartBefore = cartData.value;
      const unavailableTitles = findUnavailableTitles(cartBefore);
      if (unavailableTitles.length) {
        setCartUrlNotice({
          type: 'unavailable',
          items: unavailableTitles.map((title) => ({ title })),
        });
        return false;
      }
      const record = createCartUrlActionRecord(cartBefore, urlItems, isFreeGiftItem);
      if (!writeCartUrlActionRecord(record)) {
        setCartUrlNotice({ type: 'not-added', items: [] });
        return false;
      }
      activeCartUrlActionId = record.id;
      try {
        return await runCartUrlAction(
          record,
          urlItems.map(({ item }) => item),
          isRequestedCartSettled,
        );
      } finally {
        activeCartUrlActionId = null;
      }
    });

  const removeCartItems = async (items: string[]): Promise<ShopifyCart> => {
    dismissCartUrlNotice();
    const formattedItems: CartItemQuantity[] = items.map((key) => ({ key, quantity: 0 }));
    return updateCartItemQuantities(formattedItems);
  };

  const emptyCart = async (): Promise<ShopifyCart> => {
    const { error } = await runCartMutation(clearCart, () => true);
    if (error) console.error(error);
    return cartData.value;
  };

  return {
    refreshCart,
    addCartItems,
    updateCartItemQuantities,
    changeCartItemQuantity,
    addCartUrlItems,
    removeCartItems,
    emptyCart,
  };
};
